import { useZero, ZeroContext, ZeroProvider } from '@rocicorp/zero/react';
import { mutators, schema, type Schema } from '@strength/zero-schema';
import { PropsWithChildren, useEffect, useState } from 'react';
import { useAuth } from '../../auth';
import { environment } from '../../environment';
import { useFlags } from '../context/featureFlag';
import { ZeroCacheDebugPanel } from './ZeroCacheDebugPanel';
import { setActiveZero } from './zero-active-instance';
import {
  ZERO_STUB,
  SURFACE_ZERO_FLAGS,
  readStoredZeroEnabled,
  ZERO_ENABLED_STORAGE_KEY,
} from './zero-active';
import { deriveRoleStorageKey } from './role-storage-key';
import { ZeroSessionSync } from './ZeroSessionSync';

function persistZeroEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(ZERO_ENABLED_STORAGE_KEY, String(enabled));
  } catch {
    // best-effort: persistence is a debug convenience, not correctness
  }
}

// Registers the live Zero instance so logout (outside React context) can close
// it. Does not clear on unmount: that would race with the expired-session
// logout path — closeActiveZero clears the reference itself.
function RegisterZeroInstance() {
  const zero = useZero<Schema>();
  useEffect(() => {
    setActiveZero(zero);
  }, [zero]);
  return null;
}

export function ZeroClientProvider({ children }: PropsWithChildren) {
  const flags = useFlags();
  const { zeroFlag, zeroDebugFlag } = flags;
  const { auth } = useAuth();
  const serverUrl = environment.VITE_ZERO_CACHE_URL;

  // Mounts when `zeroFlag` OR any surface flag is on (see SURFACE_ZERO_FLAGS);
  // surface call sites gate on useIsZeroSurfaceActive.
  const anySurfaceZeroFlag = Object.values(SURFACE_ZERO_FLAGS).some(
    (flag) => flags[flag],
  );

  // Local Zero on/off toggle. A persisted override in localStorage (written
  // by the debug drawer) shadows the flags; without one, enablement is
  // derived from the *current* LaunchDarkly flags on every render, so a
  // rollout change after mount is honored (up or down) without a reload.
  // Flipping it from the debug drawer writes the override, so the choice
  // survives full page reloads (not just SPA navigation). Dev tooling only —
  // a stored override shadows the flag until localStorage is cleared.
  const [zeroOverride, setZeroOverride] = useState<boolean | null>(() =>
    readStoredZeroEnabled(),
  );
  const zeroEnabled = zeroOverride ?? (zeroFlag || anySurfaceZeroFlag);
  const onToggleZero = () =>
    setZeroOverride((override) => {
      const next = !(override ?? (zeroFlag || anySurfaceZeroFlag));
      persistZeroEnabled(next);
      return next;
    });

  // Keyed on the server-effective role+teams from the session: only a real
  // role/team change re-keys, rebuilding Zero against a fresh empty store so a
  // downgraded user can't read previously-synced rows. See deriveRoleStorageKey.
  const storageKey =
    auth?.type === 'org'
      ? deriveRoleStorageKey(auth.role, auth.teamIds)
      : 'unauthenticated';

  if (!zeroEnabled || !serverUrl || auth?.type !== 'org') {
    return (
      <ZeroContext.Provider value={ZERO_STUB}>
        {zeroDebugFlag && (
          <ZeroCacheDebugPanel
            zeroEnabled={zeroEnabled}
            onToggleZero={onToggleZero}
            showConnection={false}
          />
        )}
        {children}
      </ZeroContext.Provider>
    );
  }

  // log to know if we are using the Zero provider
  console.info('Zero Enabled');

  return (
    <ZeroProvider
      cacheURL={serverUrl}
      schema={schema}
      // Zero mutators (track.create, …) from @strength/zero-schema. One
      // definition per mutator runs in both locations: here on the client it
      // executes the optimistic local write; the server runs the authoritative
      // write at /zero/mutate via the same registry.
      mutators={mutators}
      // profileId is org-scoped, giving each user isolated IndexedDB storage per org
      userID={auth.profileId}
      context={auth}
      storageKey={storageKey}
    >
      <RegisterZeroInstance />
      {/* Refreshes the session on a role/team change → auth → storageKey → rebuild. */}
      <ZeroSessionSync />
      {zeroDebugFlag && (
        <ZeroCacheDebugPanel
          zeroEnabled={zeroEnabled}
          onToggleZero={onToggleZero}
          showConnection
        />
      )}
      {children}
    </ZeroProvider>
  );
}
